'use client';

import React, {useState, useEffect} from 'react';
import {Button} from '@udemy-v2/react-core-components';
import {Language, useI18n} from '@udemy-v2/i18n';
import {Icon} from '@udemy-v2/icons';
import styles from './course-caption-details.module.scss';
import {CourseCaption} from '@udemy-v2/browse-course';
import classNames from 'classnames';

export interface CourseCaptionDetailsProps {
    captionedLanguages: CourseCaption[];
    isExpanded?: boolean;
    onExpandChange?: (expanded: boolean) => void;
}

/**
 * CourseCaptionDetails displays the available caption languages for a course.
 * It has different implementations for desktop and mobile views.
 */
export const CourseCaptionDetails: React.FC<CourseCaptionDetailsProps> = ({
    captionedLanguages,
    isExpanded = false,
    onExpandChange,
}) => {
    const {gettext, ninterpolate} = useI18n();
    const [isLocalExpanded, setIsLocalExpanded] = useState(isExpanded);

    useEffect(() => {
        setIsLocalExpanded(isExpanded);
    }, [isExpanded]);

    const handleExpandClick = () => {
        const newExpandedState = !isLocalExpanded;
        setIsLocalExpanded(newExpandedState);
        onExpandChange?.(newExpandedState);
    };

    const renderLanguage = (language: CourseCaption, index: number, totalCount: number) => (
        <span
            key={language.locale}
            className={classNames(styles['language-display'], 'ud-text-sm', 'ud-text-subdued')}
        >
            <Language locale={language.locale} />
            {language.isAutoGenerated &&
                (index < totalCount - 1 ? gettext(' [Auto],') : gettext(' [Auto]'))}
        </span>
    );

    const VISIBLE_COUNT = 2;

    if (!captionedLanguages.length) {
        return null;
    }

    const visibleLanguages = captionedLanguages.slice(0, VISIBLE_COUNT);
    const extraLanguages = captionedLanguages.slice(VISIBLE_COUNT);

    return (
        <div
            className={classNames('ud-text-sm', styles.captions, {
                [styles.expanded]: isLocalExpanded,
            })}
            data-purpose="caption"
        >
            <div className={styles['first-row']}>
                <Icon
                    glyph="subtitles"
                    size="xsmall"
                    color="subdued-inverted"
                    label={false}
                    className={styles['subtitles-icon']}
                    role="img"
                    aria-label={gettext('Closed Captions')}
                    aria-hidden={false}
                />
                {/* Visible languages */}
                {visibleLanguages.map((language, index) =>
                    renderLanguage(language, index, visibleLanguages.length + 1),
                )}

                {/* Extra languages button when not expanded */}
                {extraLanguages.length > 0 && !isLocalExpanded && (
                    <Button
                        udStyle="link"
                        size="medium"
                        onClick={handleExpandClick}
                        className={styles['more-button']}
                        typography="ud-text-sm"
                        data-purpose="caption-more"
                    >
                        {ninterpolate(' %(count)s more', ' %(count)s more', extraLanguages.length, {
                            count: extraLanguages.length,
                        })}
                    </Button>
                )}
            </div>

            {/* Extra languages when expanded */}
            {isLocalExpanded && extraLanguages.length > 0 && (
                <div className={styles['second-row']} data-purpose="extra-languages">
                    {extraLanguages.map((language, index) =>
                        renderLanguage(language, index, extraLanguages.length),
                    )}
                </div>
            )}
        </div>
    );
};
